import React, { useState, useRef, useCallback, useEffect } from 'react';
import MainLayout from '../../components/ui/MainLayout';
import Icon from '../../components/AppIcon';
import * as XLSX from 'xlsx';
import { supabase } from '../../lib/supabase';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtime } from '../../contexts/RealtimeContext';

// ─── Template fields ──────────────────────────────────────────────────────────
const TEMPLATE_FIELDS = [
  'Full Name', 'National ID / Passport', 'Gender', 'Nationality',
  'Email', 'Phone', 'Building', 'Room Number', 'Resident Category',
  'Check-in Date', 'Check-out Date', 'Accommodation Type', 'Notes',
];

const RESIDENT_CATEGORIES = ['student', 'staff', 'faculty', 'guest', 'researcher'];
const BUILDINGS = ['F1', 'F2', 'G1', 'G2', 'H', 'L'];

// ─── CSV/Excel parser ─────────────────────────────────────────────────────────
function parseCSV(text) {
  const lines = text?.trim()?.split('\n');
  if (lines?.length < 2) return [];
  const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.replace(/^"|"$/g, ''));
  return lines?.slice(1)?.map((line, idx) => {
    const vals = line?.split(',')?.map(v => v?.trim()?.replace(/^"|"$/g, ''));
    const obj = { _rowNum: idx + 2 };
    headers?.forEach((h, i) => { obj[h] = vals?.[i] || ''; });
    return obj;
  })?.filter(r => Object.values(r)?.some(v => v && v !== ''));
}

function parseExcel(buffer) {
  const workbook = XLSX?.read(buffer, { type: 'array' });
  const sheetName = workbook?.SheetNames?.[0];
  if (!sheetName) return [];
  const sheet = workbook?.Sheets?.[sheetName];
  const data = XLSX?.utils?.sheet_to_json(sheet, { defval: '' });
  return data?.map((row, idx) => ({ ...row, _rowNum: idx + 2 }));
}

// ─── Row normalizer ───────────────────────────────────────────────────────────
function normalizeRow(row) {
  return {
    fullName: row?.['Full Name'] || row?.full_name || row?.fullName || '',
    nationalId: row?.['National ID / Passport'] || row?.['National ID'] || row?.national_id || row?.passport || '',
    gender: (row?.['Gender'] || row?.gender || '')?.toLowerCase(),
    nationality: row?.['Nationality'] || row?.nationality || '',
    email: row?.['Email'] || row?.email || '',
    phone: row?.['Phone'] || row?.phone || '',
    building: row?.['Building'] || row?.building || '',
    roomNumber: row?.['Room Number'] || row?.room_number || row?.room || '',
    residentCategory: (row?.['Resident Category'] || row?.resident_category || 'student')?.toLowerCase(),
    checkIn: row?.['Check-in Date'] || row?.check_in_date || row?.checkIn || '',
    checkOut: row?.['Check-out Date'] || row?.check_out_date || row?.checkOut || '',
    accommodationType: (row?.['Accommodation Type'] || row?.accommodation_type || 'long-term')?.toLowerCase()?.replace(' ', '-'),
    notes: row?.['Notes'] || row?.notes || '',
    _rowNum: row?._rowNum || 0,
  };
}

// ─── Row validator ────────────────────────────────────────────────────────────
function validateRow(row, idx, existingStays) {
  const errors = [];
  if (!row?.fullName?.trim()) errors?.push('Full Name is required');
  if (!row?.building?.trim()) errors?.push('Building is required');
  else if (!BUILDINGS?.includes(row?.building?.trim())) errors?.push(`Building must be one of: ${BUILDINGS?.join(', ')}`);
  if (!row?.roomNumber?.trim()) errors?.push('Room Number is required');
  if (!row?.checkIn?.trim()) errors?.push('Check-in Date is required');
  if (!row?.checkOut?.trim()) errors?.push('Check-out Date is required');
  if (row?.gender && !['male', 'female', 'other', '']?.includes(row?.gender)) errors?.push('Gender must be Male or Female');
  if (row?.checkIn && row?.checkOut && row?.checkIn >= row?.checkOut) errors?.push('Check-out must be after Check-in');

  // Check overbooking
  if (row?.building && row?.roomNumber && row?.checkIn && row?.checkOut && existingStays?.length > 0) {
    const conflict = existingStays?.find(s => {
      const sRoom = s?.room_number || s?.room;
      const sBuilding = s?.building;
      const sStatus = s?.status;
      if (sRoom !== row?.roomNumber || sBuilding !== row?.building) return false;
      if (sStatus === 'completed-stay') return false;
      const sIn = s?.check_in_date || s?.checkIn;
      const sOut = s?.check_out_date || s?.checkOut;
      return sIn < row?.checkOut && sOut > row?.checkIn;
    });
    if (conflict) errors?.push(`⚠️ Overbooking: Room ${row?.building}-${row?.roomNumber} is already occupied during this period`);
  }

  return errors;
}

// ─── Import executor ──────────────────────────────────────────────────────────
async function importRows(rows, onProgress) {
  let imported = 0;
  const errors = [];
  const total = rows?.length;

  for (let i = 0; i < rows?.length; i++) {
    const row = rows?.[i];
    if (row?._errors?.length > 0) {
      errors?.push(`Row ${row?._rowNum}: ${row?._errors?.join('; ')}`);
      onProgress(Math.round(((i + 1) / total) * 100));
      continue;
    }
    try {
      const { error } = await supabase?.from('stays')?.insert({
        resident_name: row?.fullName,
        resident_id_code: row?.nationalId || null,
        passport_id: row?.nationalId || null,
        gender: row?.gender || null,
        nationality: row?.nationality || null,
        email: row?.email || null,
        phone: row?.phone || null,
        room_number: row?.roomNumber,
        building: row?.building,
        floor: 0,
        check_in_date: row?.checkIn,
        check_out_date: row?.checkOut,
        status: 'active-reservation',
        guest_status: 'awaiting-arrival',
        accommodation_type: row?.accommodationType || 'long-term',
        resident_category: row?.residentCategory || 'student',
        financial_status: 'not_covered_by_senghor',
        notes: row?.notes || null,
        guest_id: null,
      });
      if (error) {
        errors?.push(`Row ${row?._rowNum}: ${error?.message}`);
      } else {
        imported++;
      }
    } catch (e) {
      errors?.push(`Row ${row?._rowNum}: ${e?.message}`);
    }
    onProgress(Math.round(((i + 1) / total) * 100));
  }
  return { imported, errors };
}

// ─── Download template ────────────────────────────────────────────────────────
function downloadTemplate() {
  const ws = XLSX?.utils?.aoa_to_sheet([
    TEMPLATE_FIELDS,
    ['Ahmed Ben Ali', 'TN-001234', 'Male', 'Tunisian', 'ahmed@example.com', '+216 71 000 001', 'F1', 'F1-101', 'Student', '2026-09-01', '2027-06-30', 'Long-Term', 'Sample note'],
  ]);
  const wb = XLSX?.utils?.book_new();
  XLSX?.utils?.book_append_sheet(wb, ws, 'Residents');
  XLSX?.write(wb, { bookType: 'xlsx', type: 'array' });
  const buf = XLSX?.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'long_term_residents_template.xlsx';
  a?.click();
  URL.revokeObjectURL(url);
}

// ─── Import History Row ───────────────────────────────────────────────────────
function HistoryRow({ record, language }) {
  const isFr = language === 'fr';
  const date = new Date(record?.created_at);
  const dateStr = date?.toLocaleDateString(isFr ? 'fr-FR' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  const hasErrors = record?.failed_rows > 0;

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
            <Icon name="FileSpreadsheet" size={13} className="text-blue-600" />
          </div>
          <span className="text-sm font-medium text-slate-700 truncate max-w-[180px]">{record?.file_name}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-xs text-slate-500">{dateStr}</td>
      <td className="px-4 py-3 text-xs text-slate-500">{record?.imported_by_name || '—'}</td>
      <td className="px-4 py-3">
        <span className="text-sm font-semibold text-emerald-600">{record?.imported_rows}</span>
        <span className="text-xs text-slate-400"> / {record?.total_rows}</span>
      </td>
      <td className="px-4 py-3">
        {hasErrors ? (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">
            <Icon name="AlertCircle" size={10} />
            {record?.failed_rows} {isFr ? 'erreur(s)' : 'error(s)'}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">
            <Icon name="CheckCircle" size={10} />
            {isFr ? 'Succès' : 'Success'}
          </span>
        )}
      </td>
    </tr>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function BulkImport() {
  const { language } = useLanguage();
  const { user, userProfile } = useAuth();
  const { stays, fetchStays } = useRealtime();
  const isFr = language === 'fr';

  const [activeTab, setActiveTab] = useState('import');
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [editedRows, setEditedRows] = useState([]);
  const [parseStatus, setParseStatus] = useState('idle'); // idle | parsing | preview | importing | done | error
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [passportFiles, setPassportFiles] = useState({});
  const fileInputRef = useRef();

  // ── Load import history ──────────────────────────────────────────────────
  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const { data, error } = await supabase?.from('import_history')?.select('*')?.order('created_at', { ascending: false })?.limit(50);
      if (!error && data) setImportHistory(data);
    } catch (e) {
      console.error('loadHistory error:', e);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  React.useEffect(() => {
    if (activeTab === 'history') loadHistory();
  }, [activeTab, loadHistory]);

  // ── File handling ────────────────────────────────────────────────────────
  const handleFile = async (f) => {
    if (!f) return;
    setFile(f);
    setParseStatus('parsing');
    setRows([]);
    setEditedRows([]);
    setImportResult(null);

    const ext = f?.name?.split('.')?.pop()?.toLowerCase();
    if (!['csv', 'xlsx', 'xls']?.includes(ext)) {
      setParseStatus('error');
      return;
    }

    try {
      let parsed = [];
      if (ext === 'csv') {
        const text = await f?.text();
        parsed = parseCSV(text);
      } else {
        const buffer = await f?.arrayBuffer();
        parsed = parseExcel(new Uint8Array(buffer));
      }

      if (!parsed?.length) {
        setParseStatus('error');
        return;
      }

      const normalized = parsed?.map(r => normalizeRow(r));
      const withValidation = normalized?.map((r, idx) => ({
        ...r,
        _errors: validateRow(r, idx, stays || []),
      }));

      setRows(withValidation);
      setEditedRows(withValidation);
      setParseStatus('preview');
    } catch (e) {
      console.error('Parse error:', e);
      setParseStatus('error');
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    const f = e?.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  // ── Inline edit ──────────────────────────────────────────────────────────
  const handleEditRow = (idx, field, value) => {
    setEditedRows(prev => {
      const updated = prev?.map((r, i) => {
        if (i !== idx) return r;
        const newRow = { ...r, [field]: value };
        newRow._errors = validateRow(newRow, idx, stays || []);
        return newRow;
      });
      return updated;
    });
  };

  // ── Passport photo upload ────────────────────────────────────────────────
  const handlePassportUpload = (idx, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setPassportFiles(prev => ({ ...prev, [idx]: { url: e?.target?.result, name: file?.name } }));
    };
    reader?.readAsDataURL(file);
  };

  // ── Import ───────────────────────────────────────────────────────────────
  const handleImport = async () => {
    const validRows = editedRows?.filter(r => r?._errors?.length === 0);
    const invalidRows = editedRows?.filter(r => r?._errors?.length > 0);

    if (!validRows?.length && !invalidRows?.length) return;

    setParseStatus('importing');
    setImportProgress(0);

    const result = await importRows(editedRows, setImportProgress);
    setImportResult(result);
    setParseStatus('done');

    // Save to import history
    try {
      await supabase?.from('import_history')?.insert({
        file_name: file?.name || 'unknown',
        import_type: 'long-term-residents',
        imported_by: user?.id || null,
        imported_by_name: userProfile?.full_name || user?.email || 'Admin',
        total_rows: editedRows?.length,
        imported_rows: result?.imported,
        failed_rows: result?.errors?.length,
        errors: result?.errors?.length ? result?.errors : [],
        status: result?.errors?.length === 0 ? 'completed' : 'partial',
      });
    } catch (e) {
      console.error('Failed to save import history:', e);
    }

    // Refresh stays data
    if (fetchStays) fetchStays();
  };

  const reset = () => {
    setFile(null);
    setRows([]);
    setEditedRows([]);
    setParseStatus('idle');
    setImportProgress(0);
    setImportResult(null);
    setPassportFiles({});
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };

  const validCount = editedRows?.filter(r => r?._errors?.length === 0)?.length;
  const errorCount = editedRows?.filter(r => r?._errors?.length > 0)?.length;
  const warningCount = editedRows?.filter(r => r?._errors?.some(e => e?.includes('⚠️')))?.length;

  return (
    <MainLayout>
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">

          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1e40af, #3b82f6)' }}>
                <Icon name="FileUp" size={20} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{isFr ? 'Importation en masse' : 'Bulk Import'}</h1>
                <p className="text-sm text-slate-500">{isFr ? 'Importer des résidents en hébergement longue durée via Excel/CSV' : 'Import long-term accommodation residents via Excel/CSV'}</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1 w-fit mb-6 shadow-sm">
            {[
              { id: 'import', label: isFr ? 'Importer' : 'Import', icon: 'Upload' },
              { id: 'history', label: isFr ? 'Historique' : 'History', icon: 'Clock' },
            ]?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab?.id ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <Icon name={tab?.icon} size={14} />
                {tab?.label}
              </button>
            ))}
          </div>

          {/* ── IMPORT TAB ── */}
          {activeTab === 'import' && (
            <div className="space-y-6">

              {/* Info Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
                <Icon name="Info" size={16} className="text-blue-600 mt-0.5 shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">{isFr ? 'Colonnes requises dans le fichier Excel/CSV :' : 'Required columns in your Excel/CSV file:'}</p>
                  <p className="text-xs text-blue-700 leading-relaxed">{TEMPLATE_FIELDS?.join(' · ')}</p>
                  <p className="text-xs text-blue-600 mt-1">{isFr ? 'Les dates doivent être au format YYYY-MM-DD (ex: 2026-09-01)' : 'Dates must be in YYYY-MM-DD format (e.g. 2026-09-01)'}</p>
                </div>
              </div>

              {/* Upload Zone */}
              {parseStatus === 'idle' && (
                <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-base font-semibold text-slate-800">{isFr ? 'Télécharger le fichier' : 'Upload File'}</h2>
                    <button
                      onClick={downloadTemplate}
                      className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                    >
                      <Icon name="Download" size={13} />
                      {isFr ? 'Télécharger le modèle Excel' : 'Download Excel Template'}
                    </button>
                  </div>

                  <div
                    onDrop={handleDrop}
                    onDragOver={e => e?.preventDefault()}
                    onClick={() => fileInputRef?.current?.click()}
                    className="border-2 border-dashed border-blue-200 rounded-xl p-12 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-all"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-blue-100 mx-auto mb-4 flex items-center justify-center">
                      <Icon name="FileSpreadsheet" size={26} className="text-blue-600" />
                    </div>
                    <p className="text-base font-semibold text-slate-700 mb-1">
                      {isFr ? 'Glissez votre fichier ici' : 'Drop your file here'}
                    </p>
                    <p className="text-sm text-slate-400">
                      {isFr ? 'ou cliquez pour parcourir — CSV, XLSX, XLS' : 'or click to browse — CSV, XLSX, XLS'}
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      className="hidden"
                      onChange={e => handleFile(e?.target?.files?.[0])}
                    />
                  </div>
                </div>
              )}

              {/* Parsing */}
              {parseStatus === 'parsing' && (
                <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center shadow-sm">
                  <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-slate-600">{isFr ? 'Lecture du fichier...' : 'Reading file...'}</p>
                </div>
              )}

              {/* Error */}
              {parseStatus === 'error' && (
                <div className="bg-white border border-red-200 rounded-2xl p-8 text-center shadow-sm">
                  <Icon name="AlertCircle" size={32} className="text-red-500 mx-auto mb-3" />
                  <p className="text-red-700 font-medium">{isFr ? 'Erreur de lecture du fichier' : 'Error reading file'}</p>
                  <p className="text-sm text-red-500 mt-1">{isFr ? 'Vérifiez le format et réessayez.' : 'Check the format and try again.'}</p>
                  <button onClick={reset} className="mt-4 px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm hover:bg-red-100 transition-colors">
                    {isFr ? 'Réessayer' : 'Try Again'}
                  </button>
                </div>
              )}

              {/* Preview + Edit */}
              {parseStatus === 'preview' && editedRows?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                  {/* Preview Header */}
                  <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      <Icon name="FileText" size={16} className="text-slate-400" />
                      <span className="text-sm font-semibold text-slate-700">{file?.name}</span>
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{editedRows?.length} {isFr ? 'lignes' : 'rows'}</span>
                      {validCount > 0 && <span className="text-xs bg-emerald-50 text-emerald-600 border border-emerald-200 px-2 py-0.5 rounded-full">{validCount} {isFr ? 'valides' : 'valid'}</span>}
                      {errorCount > 0 && <span className="text-xs bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 rounded-full">{errorCount} {isFr ? 'erreurs' : 'errors'}</span>}
                      {warningCount > 0 && <span className="text-xs bg-amber-50 text-amber-600 border border-amber-200 px-2 py-0.5 rounded-full">{warningCount} {isFr ? 'avertissements' : 'warnings'}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={reset} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                        <Icon name="RotateCcw" size={12} />
                        {isFr ? 'Réinitialiser' : 'Reset'}
                      </button>
                      <button
                        onClick={handleImport}
                        disabled={validCount === 0}
                        className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                      >
                        <Icon name="Upload" size={14} />
                        {isFr ? `Importer ${validCount} résident(s)` : `Import ${validCount} resident(s)`}
                      </button>
                    </div>
                  </div>

                  {/* Overbooking warning */}
                  {warningCount > 0 && (
                    <div className="mx-5 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
                      <Icon name="AlertTriangle" size={15} className="text-amber-600 mt-0.5 shrink-0" />
                      <p className="text-xs text-amber-700">
                        {isFr
                          ? `${warningCount} ligne(s) ont des avertissements de surréservation. Vérifiez les chambres avant d'importer.`
                          : `${warningCount} row(s) have overbooking warnings. Please verify room assignments before importing.`}
                      </p>
                    </div>
                  )}

                  {/* Editable Table */}
                  <div className="overflow-x-auto mt-4">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-100">
                          <th className="px-3 py-2.5 text-left font-semibold text-slate-500 whitespace-nowrap">#</th>
                          <th className="px-3 py-2.5 text-left font-semibold text-slate-500 whitespace-nowrap">{isFr ? 'Nom complet' : 'Full Name'} *</th>
                          <th className="px-3 py-2.5 text-left font-semibold text-slate-500 whitespace-nowrap">{isFr ? 'ID/Passeport' : 'ID/Passport'}</th>
                          <th className="px-3 py-2.5 text-left font-semibold text-slate-500 whitespace-nowrap">{isFr ? 'Genre' : 'Gender'}</th>
                          <th className="px-3 py-2.5 text-left font-semibold text-slate-500 whitespace-nowrap">{isFr ? 'Nationalité' : 'Nationality'}</th>
                          <th className="px-3 py-2.5 text-left font-semibold text-slate-500 whitespace-nowrap">{isFr ? 'Bâtiment' : 'Building'} *</th>
                          <th className="px-3 py-2.5 text-left font-semibold text-slate-500 whitespace-nowrap">{isFr ? 'Chambre' : 'Room'} *</th>
                          <th className="px-3 py-2.5 text-left font-semibold text-slate-500 whitespace-nowrap">{isFr ? 'Catégorie' : 'Category'}</th>
                          <th className="px-3 py-2.5 text-left font-semibold text-slate-500 whitespace-nowrap">{isFr ? 'Arrivée' : 'Check-in'} *</th>
                          <th className="px-3 py-2.5 text-left font-semibold text-slate-500 whitespace-nowrap">{isFr ? 'Départ' : 'Check-out'} *</th>
                          <th className="px-3 py-2.5 text-left font-semibold text-slate-500 whitespace-nowrap">{isFr ? 'Photo passeport' : 'Passport Photo'}</th>
                          <th className="px-3 py-2.5 text-left font-semibold text-slate-500 whitespace-nowrap">{isFr ? 'Statut' : 'Status'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editedRows?.map((row, idx) => {
                          const hasError = row?._errors?.length > 0;
                          const hasWarning = row?._errors?.some(e => e?.includes('⚠️'));
                          return (
                            <tr
                              key={idx}
                              className={`border-b border-slate-50 ${hasError ? (hasWarning ? 'bg-amber-50/50' : 'bg-red-50/50') : 'bg-white hover:bg-slate-50/50'}`}
                            >
                              <td className="px-3 py-2 text-slate-400 font-mono">{row?._rowNum}</td>
                              <td className="px-3 py-2">
                                <input
                                  value={row?.fullName}
                                  onChange={e => handleEditRow(idx, 'fullName', e?.target?.value)}
                                  className="w-full min-w-[120px] px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:border-blue-400 bg-white"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  value={row?.nationalId}
                                  onChange={e => handleEditRow(idx, 'nationalId', e?.target?.value)}
                                  className="w-full min-w-[90px] px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:border-blue-400 bg-white"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  value={row?.gender}
                                  onChange={e => handleEditRow(idx, 'gender', e?.target?.value)}
                                  className="w-full min-w-[80px] px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:border-blue-400 bg-white"
                                >
                                  <option value="">—</option>
                                  <option value="male">{isFr ? 'Homme' : 'Male'}</option>
                                  <option value="female">{isFr ? 'Femme' : 'Female'}</option>
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  value={row?.nationality}
                                  onChange={e => handleEditRow(idx, 'nationality', e?.target?.value)}
                                  className="w-full min-w-[90px] px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:border-blue-400 bg-white"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  value={row?.building}
                                  onChange={e => handleEditRow(idx, 'building', e?.target?.value)}
                                  className={`w-full min-w-[70px] px-2 py-1 text-xs border rounded focus:outline-none focus:border-blue-400 bg-white ${!row?.building ? 'border-red-300' : 'border-slate-200'}`}
                                >
                                  <option value="">—</option>
                                  {BUILDINGS?.map(b => <option key={b} value={b}>{b}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  value={row?.roomNumber}
                                  onChange={e => handleEditRow(idx, 'roomNumber', e?.target?.value)}
                                  className={`w-full min-w-[80px] px-2 py-1 text-xs border rounded focus:outline-none focus:border-blue-400 bg-white ${!row?.roomNumber ? 'border-red-300' : 'border-slate-200'}`}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  value={row?.residentCategory}
                                  onChange={e => handleEditRow(idx, 'residentCategory', e?.target?.value)}
                                  className="w-full min-w-[90px] px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:border-blue-400 bg-white"
                                >
                                  {RESIDENT_CATEGORIES?.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="date"
                                  value={row?.checkIn}
                                  onChange={e => handleEditRow(idx, 'checkIn', e?.target?.value)}
                                  className={`w-full min-w-[110px] px-2 py-1 text-xs border rounded focus:outline-none focus:border-blue-400 bg-white ${!row?.checkIn ? 'border-red-300' : 'border-slate-200'}`}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="date"
                                  value={row?.checkOut}
                                  onChange={e => handleEditRow(idx, 'checkOut', e?.target?.value)}
                                  className={`w-full min-w-[110px] px-2 py-1 text-xs border rounded focus:outline-none focus:border-blue-400 bg-white ${!row?.checkOut ? 'border-red-300' : 'border-slate-200'}`}
                                />
                              </td>
                              <td className="px-3 py-2">
                                {passportFiles?.[idx] ? (
                                  <div className="flex items-center gap-1">
                                    <Icon name="CheckCircle" size={12} className="text-emerald-500" />
                                    <span className="text-emerald-600 truncate max-w-[80px]">{passportFiles?.[idx]?.name}</span>
                                  </div>
                                ) : (
                                  <label className="flex items-center gap-1 cursor-pointer text-blue-500 hover:text-blue-700">
                                    <Icon name="Upload" size={12} />
                                    <span>{isFr ? 'Joindre' : 'Attach'}</span>
                                    <input
                                      type="file"
                                      accept="image/*,.pdf"
                                      className="hidden"
                                      onChange={e => handlePassportUpload(idx, e?.target?.files?.[0])}
                                    />
                                  </label>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                {hasError ? (
                                  <div className="group relative">
                                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${hasWarning ? 'bg-amber-50 text-amber-600 border border-amber-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                                      <Icon name={hasWarning ? 'AlertTriangle' : 'X'} size={10} />
                                      {hasWarning ? (isFr ? 'Avertissement' : 'Warning') : (isFr ? 'Erreur' : 'Error')}
                                    </span>
                                    <div className="absolute z-20 bottom-full left-0 mb-1 w-64 bg-slate-800 text-white text-xs rounded-lg p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                      {row?._errors?.map((err, i) => <p key={i} className="mb-0.5">• {err}</p>)}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">
                                    <Icon name="Check" size={10} />
                                    {isFr ? 'Valide' : 'Valid'}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Bottom action bar */}
                  <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between flex-wrap gap-3">
                    <p className="text-xs text-slate-500">
                      {isFr
                        ? `${validCount} ligne(s) prête(s) à importer · ${errorCount} erreur(s) sera ignorée(s)`
                        : `${validCount} row(s) ready to import · ${errorCount} error(s) will be skipped`}
                    </p>
                    <button
                      onClick={handleImport}
                      disabled={validCount === 0}
                      className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      <Icon name="Upload" size={15} />
                      {isFr ? `Confirmer l'importation (${validCount})` : `Confirm Import (${validCount})`}
                    </button>
                  </div>
                </div>
              )}

              {/* Importing progress */}
              {parseStatus === 'importing' && (
                <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-slate-700 font-medium">{isFr ? 'Importation en cours...' : 'Importing...'}</p>
                    <span className="text-blue-600 font-bold">{importProgress}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${importProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-2">{isFr ? 'Ne fermez pas cette page...' : 'Please do not close this page...'}</p>
                </div>
              )}

              {/* Done */}
              {parseStatus === 'done' && importResult && (
                <div className={`bg-white border rounded-2xl p-6 shadow-sm ${importResult?.errors?.length === 0 ? 'border-emerald-200' : 'border-amber-200'}`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${importResult?.errors?.length === 0 ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                      <Icon name={importResult?.errors?.length === 0 ? 'CheckCircle' : 'AlertCircle'} size={24} className={importResult?.errors?.length === 0 ? 'text-emerald-600' : 'text-amber-600'} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-bold text-slate-800 mb-1">
                        {importResult?.errors?.length === 0
                          ? (isFr ? 'Importation réussie !' : 'Import Successful!')
                          : (isFr ? 'Importation partielle' : 'Partial Import')}
                      </h3>
                      <div className="flex flex-wrap gap-4 mb-3">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-emerald-600">{importResult?.imported}</p>
                          <p className="text-xs text-slate-500">{isFr ? 'Importés' : 'Imported'}</p>
                        </div>
                        {importResult?.errors?.length > 0 && (
                          <div className="text-center">
                            <p className="text-2xl font-bold text-red-500">{importResult?.errors?.length}</p>
                            <p className="text-xs text-slate-500">{isFr ? 'Erreurs' : 'Errors'}</p>
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mb-3">
                        {isFr
                          ? 'Les résidents importés apparaissent maintenant dans Calendar View, Timeline, Active Stays et tous les modules.' :'Imported residents now appear in Calendar View, Timeline, Active Stays, and all modules.'}
                      </p>
                      {importResult?.errors?.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-3 max-h-32 overflow-y-auto">
                          {importResult?.errors?.map((err, i) => (
                            <p key={i} className="text-xs text-red-700 mb-0.5">• {err}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={reset}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Icon name="Plus" size={14} />
                      {isFr ? 'Nouvelle importation' : 'New Import'}
                    </button>
                    <button
                      onClick={() => setActiveTab('history')}
                      className="flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <Icon name="Clock" size={14} />
                      {isFr ? 'Voir l\'historique' : 'View History'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── HISTORY TAB ── */}
          {activeTab === 'history' && (
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-base font-semibold text-slate-800">{isFr ? 'Historique des importations' : 'Import History'}</h2>
                <button
                  onClick={loadHistory}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <Icon name="RefreshCw" size={12} />
                  {isFr ? 'Actualiser' : 'Refresh'}
                </button>
              </div>

              {loadingHistory ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : importHistory?.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="Clock" size={32} className="text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">{isFr ? 'Aucun historique d\'importation' : 'No import history yet'}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">{isFr ? 'Fichier' : 'File'}</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">{isFr ? 'Date' : 'Date'}</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">{isFr ? 'Importé par' : 'Imported By'}</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">{isFr ? 'Résidents' : 'Residents'}</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">{isFr ? 'Statut' : 'Status'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importHistory?.map(record => (
                        <HistoryRow key={record?.id} record={record} language={language} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
